/* global app moment qs: true */
import urllib from 'url';
import pathlib from 'path';
import _ from 'lodash';
import sanitize_html from 'sanitize-html';

const utility = module.exports;

const hasOwnProperty = Object.prototype.hasOwnProperty;
function extend(...args) {
  const to = args[0];
  let from;
  const copy = function copyValue(key) {
    if (hasOwnProperty.call(from, key)) {
      to[key] = from[key];
    }
  };
  for (let s = 1; s < args.length; s++) {
    from = Object(args[s]);
    Object.keys(from).forEach(copy);
  }
  return to;
}

function is_object_id(str) {
  return /* str instanceof ObjectId || */ !!(str && str.match && str.match(/^[0-9a-fA-F]{24}$/));
}

function item_url(item, prefix = '') {
  if (item.url) return item.url;
  if (item.type) return utility.site_url(pathlib.join(prefix, item.type, item.id || item.raw_id));
  return utility.site_url(pathlib.join(prefix, 'item', item.id || item._id));
}

function api_url(pathname) {
  return utility.site_url(pathname, app.get('api_url'));
}

function asset_url(url, options, basepath) {
  if (typeof options === 'string') {
    basepath = options;
    options = {};
  }
  // return empty string if no info
  if (!url && !basepath) return '';
  options = options || {};
  const no_min_js = options['no-min'];
  const no_version = options['no-version'];
  if (!/^https?:\/\//.test(url)) {
    // assume 'image' if objectId-like
    if (is_object_id(url)) {
      const intent_sizes = get_image_intent(options.intent) || [];
      const intent = intent_sizes.length > 0 ? '#' + intent_sizes.join('|') : '';
      const vary_image_src = site_url('/api/image/' + url, {}, intent);
      if (options.static) {
        const vary = parse_image_url(vary_image_src);
        const info = build_image_size(
          vary.url, vary.breakpoints, 1, 1200, constant.SCREEN_BREAKPOINT
        );
        return info.url;
      }
      return vary_image_src;
    }
    // for all internal assets
    const ext = pathlib.extname(url);
    switch (ext) {
      case '.js':
        if (!no_min_js && !app.get('debug')
        && pathlib.extname(pathlib.basename(url, '.js')) !== '.min') {
          url = url.slice(0, -3) + '.min.js';
        }
        break;
      default:
        // no-op
        break;
    }
    if (!no_version) {
      url = url + '?v=' + app.get('version');
    }
  }
  return utility.site_url(url, basepath);
}

function user_picture(user) {
  if (!user) return '';
  if (user.profile_picture_url) return user.profile_picture_url;
  if (user.profile_picture_id) return utility.asset_url(user.profile_picture_id);
  return '';
}

function user_full_name(user) {
  if (!user) return '';
  return _.compact([user.name, user.last_name]).join(' ');
}

function user_display_name(user, append_full = false) {
  if (!user) return '';
  const display_name = user.display_name ? user.display_name : user_full_name(user);
  if (!append_full) return display_name;
  const full_name = user_full_name(user);
  const name = display_name + (full_name === display_name ? '' : ' (' + full_name + ')');
  return name;
}

/**
 * Make slug from string.
 * Support: en, th, ja
 * @see https://gist.github.com/mathewbyrne/1280286
 * @see http://so-zou.jp/software/tech/programming/tech/regular-expression/meta-character/variable-width-encoding.htm
 * @see https://python3.wannaphong.com/2015/12/regular-expression-ภาษาไทย-และภาษาอื่น-python.html
 * @return {String} Slug
 */
const whitespace_like_regex = /[\s_]+/g;
// en: a-z, A-Z, 0-9, -
// th: Ko Kai - Sara Uu, Sara Ee - Thanthakhat, Thai Zero - Thai Nine
//     (Excludes Phintu, Thai Baht, Nikhahit, Yamakkan, Fongman, Angkhankhu, Khomut)
// ja: Kanji, Hiragana, Katakana
const nonword_regex = /[^a-z0-9\-ก-\u0E39เ-\u0E4C๐-๙亜-熙ぁ-んァ-ヶ]+/g;
function slugify(str) {
  return str.toString().toLowerCase()
    .replace(whitespace_like_regex, '-') // Replace spaces with -
    .replace(nonword_regex, '')          // Remove all non-word chars
    .replace(/\-\-+/g, '-')             // Replace multiple - with single -
    .replace(/^-+/, '')                  // Trim - from start of text
    .replace(/-+$/, '');
}

function get_image_intent(params) {
  if (typeof params === 'string') {
    params = utility.IMAGE_INTENT[params] || utility.IMAGE_INTENT.default;
  }
  return params;
}

/**
 *
 * Normalize for flat query string
 * @example
 *   b: 100                   => b=100
 *   a: [1,2,3]               => a[0]=1&a[1]=2&a[2]=3
 *   c: [{d:1,e:2},{d:3,e:4}] => c[0][d]=1&c[0][e]=2&c[1][d]=3&c[1][e]=4
 * @param {Object} query Simple input object
 * @return {Object} Flatten object
 */
function make_querystring(query) {
  const qs = {};
  _.forEach(Object.keys(query), (key) => {
    const value = query[key];
    if (Array.isArray(value)) {
      if (typeof value[0] === 'object') {
        Object.keys(value, (k) => {
          const v = value[k];
          Object.keys(v, (b) => {
            const a = v[b];
            qs[key + '[' + k + '][' + b + ']'] = a;
          });
        });
      } else {
        Object.keys(value, (k) => {
          const v = value[k];
          qs[key + '[' + k + ']'] = v;
        });
      }
    } else {
      qs[key] = value;
    }
  });
  return qs;
}

/**
 * Stringigy querystring object
 * @param  {Object} query Querystring parameters
 * @return {String}       Querystring as string
 */
function format_querystring(query) {
  const qs = [];
  _.forEach(query, (v, k) => {
    if (hasOwnProperty.call(query, k) && query[k]) {
      qs.push(k + '=' + encodeURIComponent(query[k]));
    }
  });
  return qs.join('&');
}

// function format_querystring(params) {
//   return Object.keys(params)
//     .map(k => k + '=' + params[k])
//     .join('&');
// }

function client_size() {
  const w = window;
  const d = document;
  const e = d.documentElement;
  const g = d.getElementsByTagName('body')[0];
  const x = w.innerWidth || e.clientWidth || g.clientWidth;
  const y = w.innerHeight || e.clientHeight || g.clientHeight;
  return { width: x, height: y };
}

function build_image_size(url, breakpoints, dppi, screenWidth, screenBreakpoints) {
  const qs = {};
  let i;
  let b = 0;
  let size;
  let width;
  let height;
  if (breakpoints.length > 0) {
    for (i = 1; i < screenBreakpoints.length; i++) {
      if (screenWidth >= screenBreakpoints[i]) b++;
      else break;
    }
    size = breakpoints[b].split('x');
    width = size[0];
    height = size[1];
    if (width) qs.w = width * dppi;
    if (height) qs.h = height * dppi;
  }
  const q = format_querystring(qs);
  return {
    url: url + (q ? '?' + q : ''),
    width: width,
    height: height
  };
}

function parse_image_url(url) {
  const data = {};
  const split = url ? url.split('#') : [];
  const breakpoints = [];
  let bp = [];
  let i;
  let pt;
  data.url = split[0] || '';
  if (split[1]) {
    bp = (split[1] || '').split('|');
    pt = bp[0];
    for (i = 0; i < 4; i++) {
      pt = (bp[i] || pt);
      breakpoints.push(pt);
    }
  }
  data.breakpoints = breakpoints;
  return data;
}

function site_url(pathname, basepath, query, hash) {
  if (basepath !== null && typeof basepath === 'object') {
    hash = query;
    query = basepath;
    basepath = null;
  }
  pathname = pathname || '';
  basepath = basepath || app.get('baseurl');
  query = qs.stringify(query || {}); // format_querystring(make_querystring(query || {}));
  query = query ? '?' + query.replace(/^\?/i, '') : '';
  hash = hash ? '#' + hash.replace(/^#/i, '') : '';
  if (/^https?:\/\//.test(pathname)) return pathname + query + hash;
  return basepath.replace(/\/$/, '') + '/' + pathname.replace(/^\//, '')
    + query + hash;
}

function is_touch_device() {
  return (('ontouchstart' in window)
  || (navigator.MaxTouchPoints > 0)
  || (navigator.msMaxTouchPoints > 0));
}

function device_pixel_ratio() {
  return window.devicePixelRatio || window.screen.availWidth / document.documentElement.clientWidth;
}

function cssSizeInPixel(size, refElement) {
  let rootFontSize;
  let baseFontSize;
  let m;
  let sign;
  let num;
  let unit;
  const cssSizeRegex = /^(-?)([0-9.]*[0-9])([a-z]+)?$/i;
  const matches = cssSizeRegex.exec(size.toString());
  if (matches) {
    refElement = refElement ? $(refElement)[0] : $('html')[0];
    // get root font size in pixel
    rootFontSize = window.getComputedStyle(refElement)['font-size'];
    m = cssSizeRegex.exec(rootFontSize);
    baseFontSize = m ? +m[2] : 10;
    num = matches[1] === '-' ? -matches[2] : +matches[2];
    unit = matches[3];
    if (unit === 'rem') { num = num * baseFontSize; }
    // if (unit === 'em') ...
    // when unit is empty, use how many line-heights
    if (!unit) {
      const lineheight = window.getComputedStyle(refElement)['line-height'];
      const lh = cssSizeRegex.exec(lineheight);
      num = num * +lh[2];
    }
    return num;
  }
  return size;
}

// simple unique ID
function uniqueId() {
  return Math.random().toString(36).substr(2, 9);
}

// easy password generator
function generate_password(len = 8) {
  return Math.random().toString(36).slice(-len);
}

// Get tag array from string with hashtags
function extract_tags(str) {
  const hash_regex = /\S*#(?:\[[^\]]+\]|\S+)/gi;
  return _.map(((str || '').match(hash_regex) || []).slice(0), tag => tag.slice(1));
}

// Replace hashtags string with link
function parse_tags(str) {
  const hash_regex = /\S*#(\[[^\]]+\]|\S+)/gi;
  return str.replace(hash_regex, '<a href="#tags/$1">#$1</a>');
}

function has_roles(roles = [], allowed_roles = []) {
  return _.intersection(allowed_roles, roles).length > 0;
}

function without_roles(roles = [], allowed_roles = []) {
  return _.intersection(allowed_roles, roles).length === 0;
}

function check_permission(perm_names, role) {
  if (!perm_names || !role) return false;
  if (!_.isArray(perm_names)) perm_names = [perm_names];
  for (let i = 0; i < perm_names.length; i++) {
    const name = perm_names[i];
    const perm = app.config.permission[name];
    if (!perm) return false;
    if (!perm.allow || without_roles(role, perm.allow)) return false;
  }
  return true;
}

function t(type, id, prop = null) {
  const get_path = prop ? prop : '';
  if (['cat', 'category', 'categories'].indexOf(type) >= 0) {
    return _.get(_.find(app.config.issue.categories, ['id', id]), get_path);
  }
  return id;
}

// // Convert class object to string to be used in "class" attributes
// function makeClass(obj) {
//   return _.map(_.filter(_.toPairs(obj), val => val[1]), a => a[0]).join(' ');
// }

function initial_text(str) {
  const excl = ' \t\n\r*&^#@()[\\]{}<>-_+=/\\?!:;\'"~`'
    + '\u0E40\u0E41\u0E44\u0E43\u0E42\u0E32'
    + '\u0E46\u0E4C\u0E48\u0E49\u0E4A\u0E47\u0E4D\u0E33'
    + '\u0E38\u0E39\u0E3A\u0E34\u0E35\u0E36\u0E37';
  const exclude_regexp = new RegExp(`[${excl}]`, 'g');
  return (str.toLowerCase().replace(exclude_regexp, '').slice(0, 1) || '?').toUpperCase();
}

function format_date(dt, format = 'D MMMM YYYY', locale = 'th') {
  function partial_format(mm, fm, lc) {
    let year = mm.format(fm);
    switch (locale) {
      case 'th':
        // BE = Buddhist Era
        year = +year + 543;
        break;
      default:
        year = +year;
        break;
    }
    year = String(year);
    if (fm === 'YY') {
      year = year.slice(-2);
    }
    return year;
  }
  // update current locale
  moment.locale(locale);
  const m = dt ? moment(dt) : moment();
  // find out if we need to localize year
  const match = /[Y]+/.exec(format);
  if (match) {
    const pre = format.slice(0, match.index);
    const year = match[0];
    const suf = format.slice(match.index + match[0].length);
    return (pre ? m.format(pre) : '')
      + partial_format(m, year, locale)
      + (suf ? m.format(suf) : '');
  }
  return m.format(format);
}

function format_duration(duration, forceAsUnit, toFixed = 2) {
  const format = {};
  duration = moment.duration(duration);
  if (forceAsUnit) {
    switch (forceAsUnit) {
      case 'minutes': format.unit = 'นาที'; break;
      case 'hours': format.unit = 'ชั่วโมง'; break;
      case 'days': format.unit = 'วัน'; break;
      case 'months': format.unit = 'เดือน'; break;
      default:
        throw new Error('Invalid duration format: ' + forceAsUnit);
    }
    format.time = +duration.as(forceAsUnit).toFixed(toFixed);
    format.explain = duration.humanize();
    format.code = duration.toJSON();
  } else {
    format.explain = duration.humanize();
    format.code = duration.toJSON();
    format.time = duration.months();
    format.unit = 'เดือน';
    if (format.time === 0) {
      format.time = duration.days();
      format.unit = 'วัน';
      if (format.time === 0) {
        format.time = duration.hours();
        format.unit = 'ชั่วโมง';
        if (format.time === 0) {
          format.time = duration.minutes();
          format.unit = 'นาที';
        }
      }
    }
  }
  return format;
}

extend(utility, {
  // cls: makeClass,
  url: site_url,
  item_url: item_url,
  asset_url: asset_url,
  api_url: api_url,
  user_picture: user_picture,
  user_full_name: user_full_name,
  user_display_name: user_display_name,
  parseUrl: urllib.parse,
  resolveUrl: urllib.resolve,
  formatUrl: urllib.format,
  make_querystring,
  format_querystring,
  parse_image_url,
  build_image_size,
  client_size,
  site_url,
  is_touch_device,
  device_pixel_ratio,
  cssSizeInPixel,
  uniqueId,
  generate_password,
  extract_tags,
  parse_tags,
  check_permission,
  has_roles,
  without_roles,
  sanitize_html,
  strip_tags: html => sanitize_html(html, { allowedTags: [] }),
  t,
  initial_text,
  format_date,
  format_duration
});
